/**
 * Reads a GIF file's total animation duration by parsing the GIF87a/89a
 * binary format directly (summing each frame's Graphic Control Extension
 * delay time) — no external library, no <video>/ffmpeg needed.
 * Returns seconds, or null if the file can't be parsed as a GIF.
 */
export const getGifDurationSeconds = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      try {
        resolve(parseGifDuration(new Uint8Array(reader.result)));
      } catch {
        resolve(null);
      }
    };
    // Only need the byte stream — no need to read past a sane cap for huge files.
    reader.readAsArrayBuffer(file);
  });

const skipSubBlocks = (buf, startPos) => {
  let pos = startPos;
  while (pos < buf.length) {
    const size = buf[pos];
    pos += 1;
    if (size === 0) break;
    pos += size;
  }
  return pos;
};

const parseGifDuration = (buf) => {
  if (buf.length < 13) return null;
  const signature = String.fromCharCode(buf[0], buf[1], buf[2], buf[3], buf[4], buf[5]);
  if (signature !== "GIF87a" && signature !== "GIF89a") return null;

  let pos = 6;
  pos += 4; // canvas width(2) + height(2)
  const packed = buf[pos];
  pos += 1;
  pos += 2; // background color index(1) + pixel aspect ratio(1)

  const hasGlobalColorTable = (packed & 0x80) !== 0;
  if (hasGlobalColorTable) {
    pos += 3 * Math.pow(2, (packed & 0x07) + 1);
  }

  let totalDelayCentiseconds = 0;
  const len = buf.length;

  while (pos < len) {
    const blockType = buf[pos];

    if (blockType === 0x3b) break; // Trailer

    if (blockType === 0x21) {
      // Extension Introducer
      pos += 1;
      const label = buf[pos];
      pos += 1;

      if (label === 0xf9) {
        // Graphic Control Extension — first sub-block is always 4 bytes:
        // packed(1), delayTime(2, little-endian, in 1/100s), transparentColorIndex(1)
        const blockSize = buf[pos];
        pos += 1;
        if (blockSize >= 3) {
          totalDelayCentiseconds += buf[pos + 1] | (buf[pos + 2] << 8);
        }
        pos += blockSize;
        pos = skipSubBlocks(buf, pos); // terminator (and any further sub-blocks)
      } else {
        pos = skipSubBlocks(buf, pos);
      }
    } else if (blockType === 0x2c) {
      // Image Descriptor
      pos += 1;
      pos += 8; // left(2) + top(2) + width(2) + height(2)
      const imgPacked = buf[pos];
      pos += 1;
      const hasLocalColorTable = (imgPacked & 0x80) !== 0;
      if (hasLocalColorTable) {
        pos += 3 * Math.pow(2, (imgPacked & 0x07) + 1);
      }
      pos += 1; // LZW minimum code size
      pos = skipSubBlocks(buf, pos); // image data sub-blocks
    } else {
      // Unrecognized block — stop parsing safely with whatever we've summed so far.
      break;
    }
  }

  return totalDelayCentiseconds / 100;
};
